import api from './api';
import {
  loginWithEmail,
  registerWithEmail,
  loginWithGoogle,
  loginWithApple,
  logout as firebaseLogout,
  resetPassword as firebaseResetPassword,
  resendVerificationEmail,
  getIdToken,
  onAuthChange,
  FirebaseUser,
} from './firebase';

export interface User {
  id: string;
  firebase_uid: string;
  email: string;
  name: string;
  role: 'student' | 'parent';
  email_verified: boolean;
  student_id: string | null;
  curriculum?: string;
  created_at: string;
  isAdmin?: boolean;
}

export interface CurriculumInfo {
  student_id: string;
  curriculum: string;
  display_name: string;
}

export interface SyncResponse {
  message: string;
  user: User;
  is_new: boolean;
  has_multiple_curricula: boolean;
  available_curricula: CurriculumInfo[];
}

export interface ChildProgress {
  student_id: string;
  name: string;
  email?: string;
  curriculum?: string;
  curriculum_display_name?: string;
  overall_progress: number;
  recent_activity: number;
  current_unit: number;
  streak_days: number;
}

export interface LinkRequest {
  id: string;
  parent_name: string | null;
  parent_email: string | null;
  status: string;
  requested_at: string | null;
}

export interface LinkRequestForParent {
  id: string;
  child_student_id: string;
  child_name: string;
  child_curriculum?: string;
  child_curriculum_display_name?: string;
  status: string;
  requested_at: string | null;
  responded_at: string | null;
}

export interface LinkedParentInfo {
  id: string;
  parent_name: string | null;
  parent_email: string | null;
  created_at: string | null;
}

// Sync user to backend after Firebase auth
export const syncUser = async (
  role: 'student' | 'parent' = 'student',
  curriculum?: string,
  allowCreate: boolean = true,
): Promise<SyncResponse> => {
  const body: any = { role, allow_create: allowCreate };
  if (curriculum) {
    body.curriculum = curriculum;
  }
  const response = await api.post('/api/auth/sync', body);
  return response.data;
};

// Get current user from backend
export const getMe = async (): Promise<User> => {
  const response = await api.get('/api/auth/me');
  return response.data;
};

// Get all curricula for the current user
export const getMyCurricula = async (): Promise<CurriculumInfo[]> => {
  const response = await api.get('/api/auth/my-curricula');
  return response.data.curricula;
};

// Login with email/password
export const login = async (email: string, password: string): Promise<SyncResponse> => {
  await loginWithEmail(email, password);
  // After Firebase login, sync to backend
  return syncUser();
};

// Register with email/password
export const register = async (
  email: string,
  password: string,
  _name: string,  // Name comes from Firebase profile
  role: 'student' | 'parent',
  curriculum: string = 'algebra1',
): Promise<SyncResponse> => {
  await registerWithEmail(email, password);
  // After Firebase register, sync to backend
  return syncUser(role, curriculum);
};

// Login with Google
export const googleLogin = async (
  role: 'student' | 'parent' = 'student',
  curriculum?: string,
  allowCreate: boolean = true,
): Promise<SyncResponse> => {
  // Pre-set curriculum in storage BEFORE Firebase auth to avoid race condition
  // with onAuthChange listener (only for registration when curriculum is specified)
  if (role === 'student' && curriculum) {
    localStorage.setItem('mathtutor_selected_curriculum', curriculum);
  }
  await loginWithGoogle();
  return syncUser(role, curriculum, allowCreate);
};

// Login with Apple
export const appleLogin = async (
  role: 'student' | 'parent' = 'student',
  curriculum?: string,
  allowCreate: boolean = true,
): Promise<SyncResponse> => {
  if (role === 'student' && curriculum) {
    localStorage.setItem('mathtutor_selected_curriculum', curriculum);
  }
  await loginWithApple();
  return syncUser(role, curriculum, allowCreate);
};

// Logout
export const logout = async (): Promise<void> => {
  await firebaseLogout();
  // Optionally notify backend
  try {
    await api.post('/api/auth/logout');
  } catch {
    // Ignore backend logout errors
  }
};

// Delete account
export const deleteAccount = async (): Promise<void> => {
  await api.delete('/api/auth/delete-account');
  await firebaseLogout();
};

// Password reset (Firebase handles email)
export const forgotPassword = async (email: string): Promise<void> => {
  await firebaseResetPassword(email);
};

// Resend verification email
export const resendVerification = async (): Promise<void> => {
  await resendVerificationEmail();
};

// Get Firebase ID token for API requests
export { getIdToken, onAuthChange };
export type { FirebaseUser };

// Parent API calls
export const getChildren = async (): Promise<{ children: ChildProgress[]; link_requests: LinkRequestForParent[] }> => {
  const response = await api.get('/api/parent/children');
  return response.data;
};

export const linkChild = async (childEmail: string): Promise<{ message: string }> => {
  const response = await api.post('/api/parent/link-child', { child_email: childEmail });
  return response.data;
};

export const unlinkChild = async (studentId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/api/parent/unlink-child/${studentId}`);
  return response.data;
};

export const getChildProgress = async (studentId: string): Promise<any> => {
  const response = await api.get(`/api/parent/child/${studentId}/progress`);
  return response.data;
};

export const getChildWeeklyReport = async (studentId: string): Promise<any> => {
  const response = await api.get(`/api/parent/child/${studentId}/weekly-report`);
  return response.data;
};

// Daily goals
export const setChildGoals = async (studentId: string, goals: {
  questions_per_day?: number | null;
  minutes_per_day?: number | null;
  accuracy_target?: number | null;
}): Promise<any> => {
  const response = await api.put(`/api/parent/child/${studentId}/goals`, goals);
  return response.data;
};

export const getChildGoalProgress = async (studentId: string): Promise<any> => {
  const response = await api.get(`/api/parent/child/${studentId}/goal-progress`);
  return response.data;
};

// Assignments
export const createAssignment = async (studentId: string, data: {
  title: string;
  target_unit: number;
  target_lesson?: number | null;
  num_questions?: number;
  due_date?: string | null;
}): Promise<any> => {
  const response = await api.post(`/api/parent/child/${studentId}/assignments`, data);
  return response.data;
};

export const getChildAssignments = async (studentId: string): Promise<any[]> => {
  const response = await api.get(`/api/parent/child/${studentId}/assignments`);
  return response.data;
};

export const deleteAssignment = async (studentId: string, assignmentId: string): Promise<any> => {
  const response = await api.delete(`/api/parent/child/${studentId}/assignments/${assignmentId}`);
  return response.data;
};

// AI Insights
export const getChildAIInsights = async (studentId: string, days: number = 30): Promise<any> => {
  const response = await api.get(`/api/parent/child/${studentId}/ai-insights?days=${days}`);
  return response.data;
};

// Gate bypass
export const getGateBypass = async (studentId: string): Promise<{ gates_unlocked: boolean }> => {
  const response = await api.get(`/api/parent/child/${studentId}/gate-bypass`);
  return response.data;
};

export const setGateBypass = async (studentId: string, unlocked: boolean): Promise<any> => {
  const response = await api.put(`/api/parent/child/${studentId}/gate-bypass`, {
    gates_unlocked: unlocked,
  });
  return response.data;
};

// Email preferences
export const getEmailPreferences = async (): Promise<any> => {
  const response = await api.get('/api/parent/email-preferences');
  return response.data;
};

export const setEmailPreferences = async (prefs: { weekly_report_enabled: boolean }): Promise<any> => {
  const response = await api.post('/api/parent/email-preferences', prefs);
  return response.data;
};

// Cancel a pending link request (parent)
export const cancelLinkRequest = async (studentId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/api/parent/cancel-link-request/${studentId}`);
  return response.data;
};

// Student API calls
export const getStudentLinkRequests = async (): Promise<LinkRequest[]> => {
  const response = await api.get('/api/student/link-requests');
  return response.data;
};

export const respondToLinkRequest = async (requestId: string, action: 'accept' | 'decline'): Promise<{ message: string }> => {
  const response = await api.post(`/api/student/link-requests/${requestId}/respond`, { action });
  return response.data;
};

export const getLinkedParents = async (): Promise<LinkedParentInfo[]> => {
  const response = await api.get('/api/student/linked-parents');
  return response.data;
};

export const removeLinkedParent = async (linkId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/api/student/linked-parents/${linkId}`);
  return response.data;
};
